import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { startInstagramOAuth } from '../../companies/services/companyService';
import { WhatsAppAppConnectorTab } from '../../connectors/components/WhatsAppAppConnectorTab';
import { WebChatConnectorTab } from '../../connectors/components/WebChatConnectorTab';
import { TikTokConnectorTab } from '../../connectors/components/TikTokConnectorTab';
import { AddMessengerAccountForm } from '../../connectors/components/AddMessengerAccountForm';
import { useChannelAccounts } from '../../connectors/hooks/useChannelAccounts';
import { createTelegramChannelAccount } from '../../connectors/services/channelAccountService';
import { ChannelAccountChannel } from '../../connectors/types/channelAccount';
import { connectorTabForChannel, SetupChannelId } from '../setupModel';
import styles from './WorkspaceSetupPage.module.css';

const ACCOUNT_CHANNEL: Record<SetupChannelId, ChannelAccountChannel> = {
  whatsapp: 'WhatsApp',
  instagram: 'Instagram',
  messenger: 'Messenger',
  telegram: 'Telegram',
  tiktok: 'TikTok',
  webchat: 'Webchat',
};

interface SetupConnectStepProps {
  channel: SetupChannelId;
  canManage: boolean;
  channelsOpen: boolean;
  canStartTrial: boolean;
  onStartTrial: () => Promise<unknown>;
}

const ConnectedAccounts: React.FC<{ channel: SetupChannelId; canManage: boolean }> = ({ channel, canManage }) => {
  const { t } = useTranslation('workspaces');
  const accounts = useChannelAccounts(
    ACCOUNT_CHANNEL[channel],
    canManage,
    channel === 'whatsapp' ? 'app' : undefined,
  );

  if (!canManage || accounts.loading || accounts.accounts.length === 0) {
    return null;
  }

  return (
    <p className={styles.connectedNote}>
      {t('setup.connect.alreadyConnected', {
        names: accounts.accounts.map(account => account.displayName).filter(Boolean).join(', '),
      })}
    </p>
  );
};

const InstagramConnect: React.FC<{ canManage: boolean }> = ({ canManage }) => {
  const { t } = useTranslation('workspaces');
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const connect = async () => {
    setBusy(true);
    setError(null);
    try {
      const result = await startInstagramOAuth();
      if (!result.authorizeUrl) {
        throw new Error(t('setup.connect.instagramMissing'));
      }

      window.location.assign(result.authorizeUrl);
    } catch (connectError) {
      setError(connectError instanceof Error ? connectError.message : t('setup.connect.instagramMissing'));
      setBusy(false);
    }
  };

  return (
    <div className={styles.connectSimple}>
      <p>{t('setup.connect.instagramHelp')}</p>
      <ConnectedAccounts channel="instagram" canManage={canManage} />
      {error ? <p className={styles.error}>{error}</p> : null}
      <button type="button" className="btn btn-primary" onClick={() => { void connect(); }} disabled={!canManage || busy}>
        {busy ? t('setup.connect.opening') : t('setup.connect.instagramAction')}
      </button>
    </div>
  );
};

const TelegramConnect: React.FC<{ canManage: boolean }> = ({ canManage }) => {
  const { t } = useTranslation('workspaces');
  const accounts = useChannelAccounts('Telegram', canManage);
  const [displayName, setDisplayName] = React.useState('');
  const [botToken, setBotToken] = React.useState('');
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [savedName, setSavedName] = React.useState<string | null>(null);

  const save = async () => {
    if (!displayName.trim() || !botToken.trim()) {
      setError(t('setup.connect.telegramRequired'));
      return;
    }

    setSaving(true);
    setError(null);
    try {
      await createTelegramChannelAccount({
        displayName: displayName.trim(),
        botToken: botToken.trim(),
        isDefault: accounts.accounts.length === 0,
      });
      setSavedName(displayName.trim());
      setDisplayName('');
      setBotToken('');
      await accounts.reload();
    } catch (saveError) {
      setError(saveError instanceof Error ? saveError.message : t('setup.connect.telegramFailed'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={styles.connectSimple}>
      <p>{t('setup.connect.telegramHelp')}</p>
      <ConnectedAccounts channel="telegram" canManage={canManage} />
      {savedName ? <p className={styles.connectedNote}>{t('setup.connect.telegramSaved', { name: savedName })}</p> : null}
      {error ? <p className={styles.error}>{error}</p> : null}
      <label className={styles.field}>
        <span>{t('setup.connect.telegramName')}</span>
        <input
          className="text-input"
          value={displayName}
          onChange={event => setDisplayName(event.target.value)}
          placeholder={t('setup.connect.telegramNamePlaceholder')}
          disabled={!canManage || saving}
        />
      </label>
      <label className={styles.field}>
        <span>{t('setup.connect.telegramToken')}</span>
        <input
          className="text-input"
          value={botToken}
          onChange={event => setBotToken(event.target.value)}
          placeholder="123456:ABC..."
          disabled={!canManage || saving}
          autoComplete="off"
        />
      </label>
      <button type="button" className="btn btn-primary" onClick={() => { void save(); }} disabled={!canManage || saving}>
        {saving ? t('setup.saving') : t('setup.connect.telegramAction')}
      </button>
    </div>
  );
};

const MessengerConnect: React.FC<{ canManage: boolean }> = ({ canManage }) => {
  const { t } = useTranslation('workspaces');
  const [created, setCreated] = React.useState(false);

  return (
    <div className={styles.connectSimple}>
      <p>{t('setup.connect.messengerHelp')}</p>
      <ConnectedAccounts channel="messenger" canManage={canManage} />
      {created ? <p className={styles.connectedNote}>{t('setup.connect.messengerSaved')}</p> : null}
      <div className={styles.hideCancel}>
        <AddMessengerAccountForm
          canManage={canManage}
          onCreated={() => setCreated(true)}
          onCancel={() => undefined}
        />
      </div>
    </div>
  );
};

export const SetupConnectStep: React.FC<SetupConnectStepProps> = ({
  channel,
  canManage,
  channelsOpen,
  canStartTrial,
  onStartTrial,
}) => {
  const { t } = useTranslation('workspaces');
  const [startingTrial, setStartingTrial] = React.useState(false);
  const [trialError, setTrialError] = React.useState<string | null>(null);

  const startTrial = async () => {
    setStartingTrial(true);
    setTrialError(null);
    try {
      await onStartTrial();
    } catch (startError) {
      setTrialError(startError instanceof Error ? startError.message : t('setup.plan.failed'));
    } finally {
      setStartingTrial(false);
    }
  };

  if (!channelsOpen) {
    return (
      <div className={styles.connectSimple}>
        <p className={styles.lead}>{t('setup.plan.connectBlocked')}</p>
        {trialError ? <p className={styles.error}>{trialError}</p> : null}
        {canStartTrial ? (
          <button type="button" className="btn btn-primary" onClick={() => { void startTrial(); }} disabled={startingTrial}>
            {startingTrial ? t('setup.plan.starting') : t('setup.plan.start')}
          </button>
        ) : (
          <p className={styles.hint}>
            {t('setup.plan.noPermission')}
            {' '}
            <Link to="/plans">{t('setup.plan.seePlans')}</Link>
          </p>
        )}
      </div>
    );
  }

  if (!canManage) {
    return <p className={styles.hint}>{t('setup.connect.noPermission')}</p>;
  }

  return (
    <div>
      {channel === 'whatsapp' ? null : <p className={styles.hint}>{t(`setup.connect.${channel}Intro`)}</p>}
      {channel === 'whatsapp' ? <WhatsAppAppConnectorTab canManage={canManage} emphasis="welcome" /> : null}
      {channel === 'instagram' ? <InstagramConnect canManage={canManage} /> : null}
      {channel === 'messenger' ? <MessengerConnect canManage={canManage} /> : null}
      {channel === 'telegram' ? <TelegramConnect canManage={canManage} /> : null}
      {channel === 'tiktok' ? <TikTokConnectorTab canManage={canManage} /> : null}
      {channel === 'webchat' ? <WebChatConnectorTab canManage={canManage} /> : null}
      <p className={styles.moreLink}>
        <Link to={`/admin/connectors?tab=${connectorTabForChannel(channel)}`}>
          {t('setup.connect.openFullSettings')}
        </Link>
      </p>
    </div>
  );
};
